import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Search } from 'lucide-react';
import { labTestService, labOrderService, labOrderItemService, LabTest } from '../../lib/services.lab';
import { supabase } from '../../lib/supabase';
import { useStore } from '../../lib/store';
import { admissionService, Admission } from '../../lib/services.ipd';
import { ipdService } from '../../lib/pharmacyServices';
import { getTenantIdSync } from '../../lib/tenantStore';
import toast from 'react-hot-toast';

interface Patient {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

export default function NewOrder() {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { user } = useStore();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [tests, setTests] = useState<LabTest[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedTests, setSelectedTests] = useState<LabTest[]>([]);
  const [source, setSource] = useState<'internal' | 'external' | 'self'>('internal');
  const [externalDoctor, setExternalDoctor] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState<'routine' | 'urgent' | 'stat'>('routine');
  const [loading, setLoading] = useState(false);
  const [searchPatient, setSearchPatient] = useState('');
  const [searchTest, setSearchTest] = useState('');
  const [activeAdmissions, setActiveAdmissions] = useState<Admission[]>([]);
  const [selectedAdmissionId, setSelectedAdmissionId] = useState<string>('');

  useEffect(() => {
    loadPatients();
    loadTests();
  }, []);

  const loadPatients = async () => {
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('id, name, email, phone')
        .order('name');
      
      if (error) throw error;
      
      setPatients(data || []);
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
    }
  };

  const loadTests = async () => {
    try {
      const testsData = await labTestService.getAll(true);
      setTests(testsData);
    } catch (error) {
      console.error('Failed to load tests:', error);
    }
  };

  const loadActiveAdmissionsForPatient = async (patientId: string) => {
    if (!patientId) {
      setActiveAdmissions([]);
      setSelectedAdmissionId('');
      return;
    }
    try {
      const allActive = await admissionService.getActive();
      const patientAdmissions = allActive.filter(a => a.patient_id === patientId);
      setActiveAdmissions(patientAdmissions);
      if (patientAdmissions.length === 1) {
        setSelectedAdmissionId(patientAdmissions[0].id);
      } else {
        setSelectedAdmissionId('');
      }
    } catch (error) {
      console.error('Failed to load admissions:', error);
    }
  };

  const filteredPatients = patients.filter(p =>
    p.name.toLowerCase().includes(searchPatient.toLowerCase()) ||
    p.phone?.includes(searchPatient)
  );

  const filteredTests = tests.filter(t =>
    t.name.toLowerCase().includes(searchTest.toLowerCase()) ||
    t.code.toLowerCase().includes(searchTest.toLowerCase())
  );

  const addTest = (test: LabTest) => {
    if (selectedTests.find(t => t.id === test.id)) {
      toast.error('Test already added');
      return;
    }
    setSelectedTests([...selectedTests, test]);
    setSearchTest('');
  };

  const removeTest = (testId: string) => {
    setSelectedTests(selectedTests.filter(t => t.id !== testId));
  };

  const calculateTotal = () => {
    return selectedTests.reduce((sum, test) => sum + test.price, 0);
  };

  const calculateDueAt = (priority: string, tatHours: number) => {
    const now = new Date();
    let hours = tatHours;
    if (priority === 'stat') hours = Math.min(2, tatHours);
    if (priority === 'urgent') hours = Math.min(4, tatHours);
    now.setHours(now.getHours() + hours);
    return now.toISOString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }

    if (selectedTests.length === 0) {
      toast.error('Please select at least one test');
      return;
    }

    if (source === 'external' && !externalDoctor) {
      toast.error('Please provide external doctor name');
      return;
    }

    setLoading(true);

    try {
      const tenantId = getTenantIdSync();
      const maxTatHours = Math.max(...selectedTests.map(t => t.tat_hours || 24));
      
      const order = await labOrderService.create({
        tenant_id: tenantId,
        patient_id: selectedPatient.id,
        ordered_by: source === 'internal' ? user?.id : undefined,
        external_doctor: source === 'external' ? externalDoctor : undefined,
        source,
        status: 'ordered',
        notes,
        priority,
        due_at: calculateDueAt(priority, maxTatHours),
      });

      for (const test of selectedTests) {
        await labOrderItemService.create({
          order_id: order.id,
          test_id: test.id,
          status: 'ordered',
          price: test.price,
          discount: 0
        });
      }

      if (selectedAdmissionId) {
        try {
          await ipdService.postChargeToBilling(selectedAdmissionId, {
            productId: order.id,
            quantity: selectedTests.length,
            price: calculateTotal(),
            source: 'laboratory',
            description: `Lab order - ${selectedTests.length} test(s)`
          });
          toast.success('Lab order created and charges posted to admission');
        } catch (billingError) {
          console.error('Billing integration failed:', billingError);
          toast.error('Lab order created but billing post failed. Please add charges manually.');
        }
      } else {
        toast.success('Lab order created successfully');
      }

      navigate('/lab');
    } catch (error: any) {
      console.error('Failed to create order:', error);
      const errorMessage = error?.message || 'Failed to create lab order';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="space-y-6">
      <header className="flex items-center gap-4">
        <button
          onClick={() => navigate('/lab')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">New Lab Order</h1>
          <p className="text-sm text-neutral-600">Create a new laboratory order</p>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Patient Selection */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Patient Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Patient *
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name or phone..."
                  value={searchPatient}
                  onChange={(e) => setSearchPatient(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {searchPatient && (
                <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredPatients.map(patient => (
                    <button
                      key={patient.id}
                      type="button"
                      onClick={() => {
                        setSelectedPatient(patient);
                        setSearchPatient('');
                        loadActiveAdmissionsForPatient(patient.id);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0"
                    >
                      <div className="font-medium">{patient.name}</div>
                      <div className="text-sm text-gray-600">{patient.phone}</div>
                    </button>
                  ))}
                </div>
              )}

              {selectedPatient && (
                <div className="mt-3 p-4 bg-blue-50 rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-medium">{selectedPatient.name}</div>
                    <div className="text-sm text-gray-600">{selectedPatient.phone}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedPatient(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order Source *
              </label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as any)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="internal">Internal (Our Doctor)</option>
                <option value="external">External Doctor</option>
                <option value="self">Self Request</option>
              </select>
            </div>

            {source === 'external' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  External Doctor Name *
                </label>
                <input
                  type="text"
                  value={externalDoctor}
                  onChange={(e) => setExternalDoctor(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Dr. John Doe"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority <span className="text-red-500">*</span>
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as 'routine' | 'urgent' | 'stat')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="routine">Routine</option>
                <option value="urgent">Urgent</option>
                <option value="stat">STAT</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                STAT: 2 hours | Urgent: 4 hours | Routine: Standard TAT
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admission ID (Optional - for IPD billing)
            </label>
            <select
              value={selectedAdmissionId}
              onChange={(e) => setSelectedAdmissionId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">No admission (OPD)</option>
              {activeAdmissions.map((admission) => (
                <option key={admission.id} value={admission.id}>
                  {admission.admission_no} - Ward: {admission.current_bed?.ward?.name || 'N/A'}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Select admission to automatically post charges to IPD billing
            </p>
          </div>
        </div>

        {/* Test Selection */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Select Tests</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Tests
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by test name or code..."
                  value={searchTest}
                  onChange={(e) => setSearchTest(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {searchTest && (
                <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredTests.map(test => (
                    <button
                      key={test.id}
                      type="button"
                      onClick={() => addTest(test)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{test.name}</div>
                          <div className="text-sm text-gray-600">
                            {test.code} • {test.department} • {test.sample_type}
                          </div>
                        </div>
                        <div className="text-sm font-medium text-blue-600">
                          ${test.price.toFixed(2)}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedTests.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Tests</h3>
                <div className="space-y-2">
                  {selectedTests.map(test => (
                    <div key={test.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">{test.name}</div>
                        <div className="text-sm text-gray-600">{test.code}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">${test.price.toFixed(2)}</span>
                        <button
                          type="button"
                          onClick={() => removeTest(test.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Additional Information</h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Any special instructions or notes..."
            />
          </div>
        </div>

        {/* Summary */}
        {selectedTests.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Order Summary</h2>
              <div className="text-2xl font-bold text-blue-600">
                ${calculateTotal().toFixed(2)}
              </div>
            </div>
            <div className="text-sm text-gray-600">
              {selectedTests.length} test(s) selected
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={() => navigate('/lab')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !selectedPatient || selectedTests.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Order'}
          </button>
        </div>
      </form>
    </section>
  );
}
